import useCollectionRegistry from '@rhythmic/reactivedb/collections/CollectionRegistry'
import useReactiveDbContextRegistry from '@rhythmic/reactivedb/composables/useReactiveDbContextRegistry'
import { defineNuxtPlugin, useRhythmicLogger, watch } from '#imports'

/**
 * Plugin to clean up ReactiveDB streams when user authentication state changes.
 *
 * This ensures that:
 * 1. All active shape streams are terminated when user logs out
 * 2. All collection caches are cleared
 * 3. All context disposers are called
 *
 * This prevents streams from continuing to run after logout and leaking resources.
 */
export default defineNuxtPlugin(() => {
  const logger = useRhythmicLogger('auth-reactivedb-cleanup')
  const { isSignedIn } = useAuth()

  // Track previous auth state to detect logout
  let wasSignedIn = isSignedIn.value

  watch(
    isSignedIn,
    async (newIsSignedIn) => {
      // Detect logout: was signed in, now not signed in
      if (wasSignedIn && !newIsSignedIn) {
        logger.info('User signed out, cleaning up ReactiveDB streams')

        try {
          // Clean up collection registry (stops all shape streams)
          const collectionRegistry = useCollectionRegistry()
          const evictedCount = collectionRegistry.evictAll()
          logger.debug('Evicted collections', { count: evictedCount })

          // Clean up context registry (calls all disposers)
          const contextRegistry = useReactiveDbContextRegistry()
          await contextRegistry.terminateAllContexts()
          logger.debug('Terminated all contexts')

          logger.info('ReactiveDB cleanup complete')
        } catch (error) {
          logger.error('Failed to clean up ReactiveDB on logout', { error })
        }
      }

      wasSignedIn = newIsSignedIn
    },
    { immediate: false },
  )
})
